import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Fade,
  Alert,
  useTheme,
  SelectChangeEvent,
  Divider
} from '@mui/material'
import {
  AddCircleOutline as AddIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  DirectionsCar as CarIcon,
  DateRange as DateIcon,
  AttachMoney as MoneyIcon,
  Notes as NotesIcon
} from '@mui/icons-material'
import { format } from 'date-fns'
import { PaymentMethod, PAYMENT_METHODS, BANKS, Vehicle, PAYMENT_STATUS, PaymentStatus, SaleCommissionType, SALE_COMMISSION_TYPE_LABELS, Employee, Sale } from '../../types'
import saleService from '../../services/saleService'
import vehicleService from '../../services/vehicleService'
import fipeService from '../../services/fipeService'
import employeeService from '../../services/employeeService'
import { isConsignmentCommissionFixed } from '../../utils/consignmentCommission'

/** Preço sugerido para a venda: anúncio normal, promo ou nenhum. */
const getSuggestedSalePriceFromVehicle = (v: Vehicle): number => {
  const sale = Number(v.sale_price)
  const promo = Number(v.sale_price_promo)
  if (Number.isFinite(sale) && sale > 0) return sale
  if (Number.isFinite(promo) && promo > 0) return promo
  return 0
}

/** Monta o vetor de valores por parcela a partir da API (array, JSON ou valor único legado). */
const parseInstallmentAmountsFromSale = (sale: Sale, count: number): number[] => {
  const n = Math.max(1, count)
  let amounts: number[] = []

  const raw = sale.installment_amounts
  if (Array.isArray(raw)) {
    amounts = raw.map((x) => Number(x))
  } else if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) amounts = parsed.map((x) => Number(x))
    } catch {
      amounts = []
    }
  }

  if (amounts.length === 0) {
    const single = Number(sale.installment_amount)
    if (Number.isFinite(single) && single > 0) {
      amounts = Array(n).fill(single)
    } else {
      amounts = Array(n).fill(0)
    }
  }

  if (amounts.length < n) {
    const pad = amounts[amounts.length - 1] ?? 0
    while (amounts.length < n) amounts.push(pad)
  } else if (amounts.length > n) {
    amounts = amounts.slice(0, n)
  }

  return amounts
}

const PaymentMethodIcon = ({ method }: { method: PaymentMethod }) => {
  switch (method) {
    case 'money': return <PaymentIcon sx={{ color: '#4caf50' }} />
    case 'pix': return <PaymentIcon sx={{ color: '#32BCAD' }} />
    case 'bank_transfer': return <PaymentIcon sx={{ color: '#2196f3' }} />
    case 'check': return <PaymentIcon sx={{ color: '#ff9800' }} />
    case 'financing': return <PaymentIcon sx={{ color: '#9c27b0' }} />
    case 'term': return <DateIcon sx={{ color: '#00897b' }} />
    default: return <PaymentIcon sx={{ color: '#757575' }} />
  }
}

interface SaleFormData {
  vehicle_id: number
  sale_price: number
  sale_date: string
  buyer_name: string
  buyer_phone: string
  buyer_document: string
  buyer_email: string
  buyer_address: string
  buyer_city: string
  buyer_state: string
  buyer_zip: string
  payment_method: PaymentMethod
  payment_status: PaymentStatus
  notes?: string
  seller_id: number | ''
  commission_type: SaleCommissionType | ''
  commission_value: number | ''
  down_payment: number,
  financed_amount: number,
  bank: string,
  bank_commission_percent: number,
  /** Data do vencimento da próxima prestação (forma a prazo) */
  next_installment_due_date: string
  /** Parcelas após a entrada (forma a prazo) */
  installment_count: number
  /** Valor de cada parcela após a entrada (índice 0 = 1ª parcela) */
  installment_amounts: number[]
}

const SaleForm: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id

  const [formData, setFormData] = useState<SaleFormData>({
    vehicle_id: 0,
    sale_price: 0,
    sale_date: format(new Date(), 'yyyy-MM-dd'),
    buyer_name: '',
    buyer_phone: '',
    buyer_document: '',
    buyer_email: '',
    buyer_address: '',
    buyer_city: '',
    buyer_state: '',
    buyer_zip: '',
    payment_method: 'money',
    payment_status: 'pending',
    notes: '',
    seller_id: '',
    commission_type: '',
    commission_value: '',
    down_payment: 0,
    financed_amount: 0,
    bank: '',
    bank_commission_percent: 0,
    next_installment_due_date: '',
    installment_count: 1,
    installment_amounts: [0]

  })

  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fipePrice, setFipePrice] = useState<string | null>(null)
  const [documents, setDocuments] = useState<{ file: File, type: string }[]>([])
  const [existingDocs, setExistingDocs] = useState<any[]>([])
  const [docsToDelete, setDocsToDelete] = useState<number[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        const [vehiclesData, employeesData] = await Promise.all([
          vehicleService.getAvailableVehicles(),
          employeeService.getAllEmployees(),
        ])
        setVehicles(vehiclesData)
        setEmployees(employeesData)

        if (isEditing) {
          const saleData = await saleService.getSale(Number(id))
          const installmentCount =
            saleData.installment_count != null
              ? Math.max(1, Math.floor(Number(saleData.installment_count)))
              : 1

          setFormData({
            vehicle_id: saleData.vehicle_id,
            sale_price: saleData.sale_price,
            sale_date: saleData.sale_date.slice(0, 10),
            buyer_name: saleData.buyer_name,
            buyer_phone: saleData.buyer_phone || '',
            buyer_document: saleData.buyer_document,
            buyer_email: saleData.buyer_email ?? '',
            buyer_address: saleData.buyer_address ?? '',
            buyer_city: saleData.buyer_city ?? '',
            buyer_state: saleData.buyer_state ?? '',
            buyer_zip: saleData.buyer_zip ?? '',
            payment_method: saleData.payment_method,
            payment_status: saleData.payment_status,
            notes: saleData.notes || '',
            seller_id: saleData.seller_id ?? '',
            commission_type: saleData.commission_type ?? '',
            commission_value: saleData.commission_value ?? '',
            down_payment: saleData.down_payment ?? '',
            financed_amount: saleData.financed_amount ?? '',
            bank: saleData.bank ?? '',
            bank_commission_percent: saleData.bank_commission_percent ?? 0,
            next_installment_due_date: saleData.next_installment_due_date
              ? String(saleData.next_installment_due_date).slice(0, 10)
              : '',
            installment_count: installmentCount,
            installment_amounts: parseInstallmentAmountsFromSale(saleData, installmentCount)
          })

          if (saleData.vehicle) setSelectedVehicle(saleData.vehicle)
          else if (saleData.vehicle_id) {
            const v = await vehicleService.getVehicle(saleData.vehicle_id)
            setSelectedVehicle(v)
          }

          if (saleData.documents) setExistingDocs(saleData.documents)
        }

        setLoading(false)
      } catch {
        setError('Não foi possível carregar os dados.')
        setLoading(false)
      }
    }

    fetchData()
  }, [id, isEditing])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDocumentChange = (event: { target: { value: string } }) => {
    let value = event.target.value.replace(/\D/g, '').slice(0, 14)

    if (value.length <= 3) value = value.replace(/(\d{0,3})/, '$1')
    else if (value.length <= 6) value = value.replace(/(\d{3})(\d{0,3})/, '$1.$2')
    else if (value.length <= 9) value = value.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3')
    else if (value.length <= 11) value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4')
    else value = value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5')

    setFormData(prev => ({ ...prev, buyer_document: value }))
  }


  const handleAddDocument = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    const file = e.target.files[0]
    const type = prompt('Tipo do documento?') || 'outro'
    setDocuments(prev => [...prev, { file, type }])
  }

  const removeExistingDoc = (id: number) => {
    setExistingDocs(prev => prev.filter(d => d.id !== id))
    setDocsToDelete(prev => [...prev, id])
  }

  const handleRemoveDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index))
  }

  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleVehicleChange = async (_: any, newValue: Vehicle | null) => {
    setSelectedVehicle(newValue)
    setFormData((prev) => {
      const vehicleId = newValue?.id || 0
      if (!newValue) {
        if (!isEditing) {
          return { ...prev, vehicle_id: 0, sale_price: 0 }
        }
        return { ...prev, vehicle_id: 0 }
      }
      const suggested = getSuggestedSalePriceFromVehicle(newValue)
      return {
        ...prev,
        vehicle_id: vehicleId,
        ...(!isEditing ? { sale_price: suggested > 0 ? suggested : 0 } : {}),
      }
    })
    if (errors.sale_price) setErrors((e) => ({ ...e, sale_price: '' }))
    if (errors.vehicle_id) setErrors((e) => ({ ...e, vehicle_id: '' }))

    if (newValue) {
      try {
        const result = await fipeService.getVehicleHistory(newValue.vehicleType, newValue.codigo_fipe, newValue.codigo_ano)
        setFipePrice(result.priceHistory[0].price)
      } catch { }
    } else {
      setFipePrice(null)
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.vehicle_id) newErrors.vehicle_id = 'Selecione veículo'
    if (!formData.sale_price || formData.sale_price <= 0) newErrors.sale_price = 'Valor inválido'
    if (!formData.sale_date) newErrors.sale_date = 'Data obrigatória'
    if (!formData.buyer_name.trim()) newErrors.buyer_name = 'Nome obrigatório'
    if (!formData.buyer_phone.trim()) newErrors.buyer_phone = 'Contato obrigatório'
    if (!formData.buyer_document.trim()) newErrors.buyer_document = 'Documento obrigatório'
    if (formData.payment_method === 'term') {
      if (!formData.down_payment || formData.down_payment <= 0) {
        newErrors.down_payment = 'Informe o valor da entrada'
      }
      if (!formData.next_installment_due_date?.trim()) {
        newErrors.next_installment_due_date = 'Informe a data da próxima prestação'
      }
      const parcels = Number(formData.installment_count)
      if (!Number.isFinite(parcels) || parcels < 1 || !Number.isInteger(parcels)) {
        newErrors.installment_count = 'Informe quantas parcelas (número inteiro ≥ 1)'
      } else {
        const amounts = formData.installment_amounts
        if (amounts.length !== parcels) {
          newErrors.installment_amounts = 'Ajuste o número de parcelas ou os valores'
        } else {
          for (let i = 0; i < parcels; i++) {
            const a = amounts[i]
            if (a == null || !Number.isFinite(a) || a <= 0) {
              newErrors.installment_amounts = `Informe o valor da parcela ${i + 1}`
              break
            }
          }
        }
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    const digits = value.replace(/\D/g, '')
    const numberValue = Number(digits) / 100

    setFormData((prev) => ({
      ...prev,
      [name]: numberValue
    }))
  }

  const handleInstallmentAmountChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '')
    const numberValue = Number(digits) / 100
    setFormData((prev) => {
      const next = [...prev.installment_amounts]
      next[index] = numberValue
      return { ...prev, installment_amounts: next }
    })
    if (errors.installment_amounts) {
      setErrors((prev) => ({ ...prev, installment_amounts: '' }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      setSaving(true)
      const data = new FormData()

      Object.entries(formData).forEach(([k, v]) => {
        if (k === 'installment_amounts') return
        if (
          formData.payment_method !== 'term' &&
          (k === 'next_installment_due_date' || k === 'installment_count')
        ) {
          data.append(k, '')
          return
        }
        data.append(k, String(v ?? ''))
      })
      data.append(
        'installment_amounts',
        formData.payment_method === 'term' ? JSON.stringify(formData.installment_amounts) : ''
      )

      documents.forEach((doc, i) => {
        data.append(`documents_add[${i}][file]`, doc.file)
        data.append(`documents_add[${i}][type]`, doc.type)
      })

      docsToDelete.forEach((id, i) => {
        data.append(`documents_delete[${i}]`, String(id))
      })

      if (isEditing) await saleService.updateSale(Number(id), data)
      else await saleService.createSale(data)

      setSuccessMessage(isEditing ? 'Venda atualizada!' : 'Venda criada!')
      setTimeout(() => navigate('/vendas'), 1500)
    } catch {
      setError('Erro ao salvar')
      setSaving(false)
    }
  }

  const handleCancel = () => navigate('/vendas')

  if (loading)
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}><CircularProgress /></Box>

  return (
    <Fade in timeout={500}>
      <div>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button startIcon={<BackIcon />} onClick={handleCancel}>Voltar</Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {isEditing ? 'Editar Venda' : 'Nova Venda'}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

        <Paper sx={{ p: 3, position: 'relative' }}>
          {saving && <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#ffffffaa', zIndex: 1 }}><CircularProgress /></Box>}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6"><CarIcon /> Veículo</Typography>

                <Autocomplete
                  options={vehicles}

                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecione o veículo"
                      required
                      error={!!errors.vehicle_id}
                      helperText={errors.vehicle_id}
                    />)}

                  getOptionLabel={o => `${o.brand} ${o.model} (${o.year})`}
                  value={selectedVehicle}
                  onChange={handleVehicleChange}
                />
              </Grid>

              {selectedVehicle && (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Marca/Modelo
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedVehicle.brand} {selectedVehicle.model}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ano
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedVehicle.year}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Placa
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedVehicle.plate}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Preço de Compra
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatCurrency(selectedVehicle.purchase_price || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  name="sale_price"
                  required
                  label="Valor de venda"
                  value={formatCurrency(formData.sale_price)}
                  onChange={handleNumberChange}
                  fullWidth
                  error={!!errors.sale_price}
                  helperText={
                    errors.sale_price
                      ? errors.sale_price
                      : !isEditing && selectedVehicle
                        ? 'Sugerido pelo preço do veículo no cadastro; você pode alterar.'
                        : undefined
                  }
                />
              </Grid>

              <Grid item xs={6}>
                <TextField type="date" name="sale_date" label="Data" value={formData.sale_date} onChange={handleInputChange} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Informações do cliente
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="buyer_name" required label="Nome completo" value={formData.buyer_name} onChange={handleInputChange} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="buyer_phone" required label="Telefone" value={formData.buyer_phone} onChange={handleInputChange} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="buyer_document"
                  label="CPF/CNPJ"
                  value={formData.buyer_document}
                  onChange={handleDocumentChange}
                  required
                  type="text"
                  fullWidth
                  error={!!errors.buyer_document}
                  helperText={errors.buyer_document}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="buyer_email" label="E-mail" type="email" value={formData.buyer_email} onChange={handleInputChange} fullWidth placeholder="cliente@email.com" />
              </Grid>
              <Grid item xs={12}>
                <TextField name="buyer_address" label="Endereço" value={formData.buyer_address} onChange={handleInputChange} fullWidth placeholder="Rua, número, bairro" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField name="buyer_city" label="Cidade" value={formData.buyer_city} onChange={handleInputChange} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField name="buyer_state" label="Estado (UF)" value={formData.buyer_state} onChange={handleInputChange} fullWidth placeholder="SP" inputProps={{ maxLength: 2 }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField name="buyer_zip" label="CEP" value={formData.buyer_zip} onChange={handleInputChange} fullWidth placeholder="00000-000" inputProps={{ maxLength: 9 }} />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="primary" fontSize="small" />
                  Pagamento do veículo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Como o cliente está pagando o carro (independente da comissão do vendedor).
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status do pagamento</InputLabel>
                  <Select name="payment_status" label="Status do pagamento" value={formData.payment_status} onChange={handleSelectChange}>
                    {Object.entries(PAYMENT_STATUS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  name="payment_method"
                  label="Forma de pagamento"
                  value={formData.payment_method}
                  onChange={handleChange}
                  fullWidth
                  required
                >
                  {Object.entries(PAYMENT_METHODS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {formData.payment_method === 'financing' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="down_payment"
                      label="Valor de entrada"
                      value={formatCurrency(formData.down_payment)}
                      onChange={handleNumberChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="financed_amount"
                      label="Valor financiado"
                      value={formatCurrency(formData.financed_amount)}
                      onChange={handleNumberChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      name="bank"
                      label="Banco"
                      value={formData.bank}
                      onChange={handleChange}
                      fullWidth
                    >
                      {Object.entries(BANKS).map(([key, bank]) => (
                        <MenuItem key={key} value={key}>
                          {bank.name} ({bank.commission}%)
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}

              {formData.payment_method === 'term' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="down_payment"
                      label="Valor da entrada"
                      value={formatCurrency(formData.down_payment)}
                      onChange={handleNumberChange}
                      fullWidth
                      required
                      error={!!errors.down_payment}
                      helperText={errors.down_payment || 'Valor pago à vista na compra'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="date"
                      name="next_installment_due_date"
                      label="Vencimento da próxima prestação"
                      value={formData.next_installment_due_date}
                      onChange={handleInputChange}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.next_installment_due_date}
                      helperText={
                        errors.next_installment_due_date ||
                        'Data em que vence a parcela seguinte à entrada'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="installment_count"
                      type="number"
                      label="Número de parcelas"
                      value={formData.installment_count}
                      onChange={(e) => {
                        const raw = e.target.value
                        const n = raw === '' ? 0 : parseInt(raw, 10)
                        const nextCount = Number.isFinite(n) ? n : 0
                        setFormData((prev) => {
                          if (nextCount < 1) {
                            return { ...prev, installment_count: nextCount }
                          }
                          let amounts = [...prev.installment_amounts]
                          if (amounts.length < nextCount) {
                            const pad = amounts.length > 0 ? amounts[amounts.length - 1] : 0
                            while (amounts.length < nextCount) amounts.push(pad)
                          } else if (amounts.length > nextCount) {
                            amounts = amounts.slice(0, nextCount)
                          }
                          return {
                            ...prev,
                            installment_count: nextCount,
                            installment_amounts: amounts
                          }
                        })
                        if (errors.installment_count) {
                          setErrors((prev) => ({ ...prev, installment_count: '' }))
                        }
                        if (errors.installment_amounts) {
                          setErrors((prev) => ({ ...prev, installment_amounts: '' }))
                        }
                      }}
                      fullWidth
                      required
                      inputProps={{ min: 1, step: 1 }}
                      error={!!errors.installment_count}
                      helperText={
                        errors.installment_count ||
                        'Quantidade de parcelas após a entrada'
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {formData.installment_count > 1
                        ? 'Valor de cada parcela (você pode definir valores diferentes)'
                        : 'Valor da parcela'}
                    </Typography>
                    {!!errors.installment_amounts && (
                      <FormHelperText error sx={{ mb: 1 }}>
                        {errors.installment_amounts}
                      </FormHelperText>
                    )}
                  </Grid>
                  {formData.installment_count >= 1 &&
                    formData.installment_amounts.map((amt, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <TextField
                          label={`Parcela ${idx + 1}`}
                          value={formatCurrency(amt)}
                          onChange={(e) => handleInstallmentAmountChange(idx, e.target.value)}
                          fullWidth
                          required
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MoneyIcon fontSize="small" color="action" />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                    ))}
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Responsável pela venda e comissão
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Responsável pela venda</InputLabel>
                  <Select
                    name="seller_id"
                    label="Responsável pela venda"
                    value={formData.seller_id === '' ? '' : String(formData.seller_id)}
                    onChange={(e) => setFormData(prev => ({ ...prev, seller_id: e.target.value === '' ? '' : Number(e.target.value) }))}
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>{emp.name} {emp.role === 'gerente' ? '(Gerente)' : ''}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de comissão</InputLabel>
                  <Select
                    name="commission_type"
                    label="Tipo de comissão"
                    value={formData.commission_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, commission_type: e.target.value as SaleCommissionType }))}
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {(Object.entries(SALE_COMMISSION_TYPE_LABELS) as [SaleCommissionType, string][]).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="commission_value"
                  label={formData.commission_type === 'fixed' ? 'Valor da comissão (R$)' : 'Valor (%)'}
                  type="number"
                  value={formData.commission_value === '' ? '' : formData.commission_value}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : parseFloat(e.target.value)
                    setFormData(prev => ({ ...prev, commission_value: v }))
                  }}
                  fullWidth
                  inputProps={{ min: 0, step: formData.commission_type === 'fixed' ? 50 : 0.5 }}
                  InputProps={formData.commission_type === 'fixed' ? { startAdornment: <InputAdornment position="start">R$</InputAdornment> } : { endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  disabled={!formData.commission_type}
                />
              </Grid>
              {formData.commission_type && formData.commission_value !== '' && Number(formData.commission_value) >= 0 && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                    {(() => {
                      const salePrice = formData.sale_price || 0
                      const purchasePrice = selectedVehicle?.purchase_price ?? 0
                      const totalExpenses = selectedVehicle?.expenses?.reduce((s, e) => s + e.amount, 0) ?? 0
                      const isConsignado = selectedVehicle?.is_consignado && selectedVehicle?.consignment_commission != null
                      const consignCommission = isConsignado
                        ? (isConsignmentCommissionFixed(selectedVehicle?.consignment_commission_type)
                          ? Number(selectedVehicle!.consignment_commission || 0)
                          : salePrice * (Number(selectedVehicle!.consignment_commission || 0) / 100))
                        : 0
                      const profit = isConsignado
                        ? consignCommission - totalExpenses
                        : salePrice - purchasePrice - totalExpenses
                      const val = Number(formData.commission_value)
                      let commissionAmount = 0
                      if (formData.commission_type === 'fixed') commissionAmount = val
                      else if (formData.commission_type === 'percent_sale') commissionAmount = salePrice * (val / 100)
                      else commissionAmount = Math.max(0, profit) * (val / 100)
                      return (
                        <>
                          Comissão estimada (saída da loja): <strong>{formatCurrency(commissionAmount)}</strong>
                          {formData.commission_type === 'percent_profit' && (
                            <> — Lucro líquido: {formatCurrency(Math.max(0, profit) - commissionAmount)}</>
                          )}
                        </>
                      )
                    })()}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField name="notes" label="Obs" value={formData.notes || ''} onChange={handleInputChange} fullWidth multiline rows={3} />
              </Grid>

              <Grid item xs={12}>
                <Button variant="outlined" component="label">
                  Anexar
                  <input hidden type="file" onChange={handleAddDocument} />
                </Button>
              </Grid>

              <Grid item xs={12}>
                {existingDocs.map(d => (
                  <Box key={d.id} display="flex" gap={1}>
                    <a href={d.url} target="_blank" rel="noreferrer">{d.type}</a>
                    <Button color="error" onClick={() => removeExistingDoc(d.id)}>Remover</Button>
                  </Box>
                ))}
                {documents.map((d, i) => (
                  <Box key={i} display="flex" gap={1}>
                    {d.type} - {d.file.name}
                    <Button color="error" onClick={() => handleRemoveDocument(i)}>Remover</Button>
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={handleCancel}>Cancelar</Button>
                <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
                  {isEditing ? 'Atualizar' : 'Salvar'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </div>
    </Fade>
  )
}

export default SaleForm
